import React from 'react';

import { RELEASE_PLATFORM } from '../../constants/releasePlatform';
import { RELEASE_STATUS } from '../../constants/releaseStatus';
import { ReleaseInfo } from '../../interfaces/ReleaseInfo';

/**
 * @description Releases are maintained on the front end — there is no API for them.
 * Source of the content: `SWYMO Releases & Updates` Google Sheet. Order follows the sheet:
 * released newest first, then upcoming nearest first.
 * Sheet mapping: `Web` → DASHBOARD, `Live` → RELEASED; the `Name` column is not shown.
 * `releaseDate` is rendered as-is: released dates use the platform `localDataFormat`
 * (`MMM DD YYYY`), upcoming releases keep the season wording.
 *
 * TODO: move `releaseDate` back to a real date (`YYYY-MM-DD`) and format it on render.
 * That needs a mechanism to still show UPCOMING releases as a season ('Summer 2026')
 * instead of the exact date — e.g. derive the season from the date, or a separate
 * display field.
 *
 * `shortNotes` is what the table row shows — keep it to a sentence or two and to inline
 * content, since the row clamps it to two lines. Set `fullNotes` only when there is more
 * to read, since it is what puts the `See More` button on the row.
 */
export const releasesList: ReleaseInfo[] = [
  {
    releaseId: 9,
    platform: RELEASE_PLATFORM.DASHBOARD,
    releaseDate: 'Oct 21 2026',
    status: RELEASE_STATUS.RELEASED,
    shortNotes: 'V1 of the progress reports are related solely to InBody metrics',
    fullNotes: (
      <>
        <p>V1 of the progress reports are related solely to InBody metrics.</p>
        <p>
          They include trends between scans, snapshots of metric subsets, and a coaching
          summary with action steps. These were created to make check-ins simple and to
          provide something of tangible value from a coach to client. Coaches should use
          these to guide clients using whatever services, or protocols, they recommend.
        </p>
      </>
    ),
    videoLink: 'https://www.youtube.com/embed/NttLcl21A60',
  },
  {
    releaseId: 10,
    platform: RELEASE_PLATFORM.APP,
    releaseDate: 'Oct 08 2026',
    status: RELEASE_STATUS.RELEASED,
    shortNotes: 'Clients will be able to track habits in their app',
    fullNotes: null,
    videoLink: 'https://www.youtube.com/embed/DMitne5ewdk',
  },
  {
    releaseId: 12,
    platform: RELEASE_PLATFORM.DASHBOARD,
    releaseDate: 'Sep 02 2026',
    status: RELEASE_STATUS.RELEASED,
    shortNotes:
      'Clients will be able to download, and login, to the SWYMO app as long as they have done an InBody scan on your machine',
    fullNotes: (
      <>
        <p>
          Clients will be able to download, and login, to the SWYMO app as long as they
          have done an InBody scan on your machine and that scan is on the LB Web.
        </p>
        <p>
          This means they do NOT have to register for ScanIQ prior to logging into the
          SWYMO app. However, they may still need to fill out missing profile information.
        </p>
      </>
    ),
    videoLink: null,
  },
  {
    releaseId: 8,
    platform: RELEASE_PLATFORM.APP,
    releaseDate: 'Jul 14 2026',
    status: RELEASE_STATUS.RELEASED,
    shortNotes: 'Snapshots for each metric subset, SWYMO Score, and a global one',
    fullNotes: (
      <>
        <p>Snapshots for each metric subset, SWYMO Score, and a global one.</p>
        <p>
          These are user-facing summaries meant to highlight areas for improvement and
          create action steps. Look at the global snapshot first to determine which areas
          have need for improvement. Then, look at the corresponding metric subsets to see
          where to take action.
        </p>
      </>
    ),
    videoLink: null,
  },
  {
    releaseId: 7,
    platform: RELEASE_PLATFORM.APP,
    releaseDate: 'Jun 24 2026',
    status: RELEASE_STATUS.RELEASED,
    shortNotes: 'Customers have an option to review previous scans in the SWYMO app',
    fullNotes: null,
    videoLink: null,
  },
  {
    releaseId: 6,
    platform: RELEASE_PLATFORM.APP,
    releaseDate: 'Jun 16 2026',
    status: RELEASE_STATUS.RELEASED,
    shortNotes:
      'SWYMO app is released and ready for download in the Apple App Store and Google Play Store',
    fullNotes: (
      <>
        <p>
          SWYMO app is released and ready for download in the Apple App Store and Google
          Play Store.
        </p>
        <p>
          In this initial release, in order to use the app, you must register for the
          ScanIQ system first.
        </p>
      </>
    ),
    videoLink: null,
  },
  {
    releaseId: 5,
    platform: RELEASE_PLATFORM.DASHBOARD,
    releaseDate: 'Jun 15 2026',
    status: RELEASE_STATUS.RELEASED,
    shortNotes: 'SWYMO for business owners is officially open for Early Access',
    fullNotes: null,
    videoLink: null,
  },
  {
    releaseId: 4,
    platform: RELEASE_PLATFORM.DASHBOARD,
    releaseDate: 'May 25 2026',
    status: RELEASE_STATUS.RELEASED,
    shortNotes:
      'A solution that allows businesses to provide their customer unlimited access to scanning',
    fullNotes: null,
    videoLink: null,
  },
  {
    releaseId: 3,
    platform: RELEASE_PLATFORM.DASHBOARD,
    releaseDate: 'May 18 2026',
    status: RELEASE_STATUS.RELEASED,
    shortNotes:
      'Added an option for coaches to download InBody Result sheets from SWYMO coaching dashboard',
    fullNotes: null,
    videoLink: null,
  },
  {
    releaseId: 2,
    platform: RELEASE_PLATFORM.DASHBOARD,
    releaseDate: 'May 07 2026',
    status: RELEASE_STATUS.RELEASED,
    shortNotes:
      'Resource video library that contains all information about the SWYMO system',
    fullNotes: null,
    videoLink: null,
  },
  {
    releaseId: 1,
    platform: RELEASE_PLATFORM.DASHBOARD,
    releaseDate: 'Feb 24 2026',
    status: RELEASE_STATUS.RELEASED,
    shortNotes: 'Release of the Challenge module',
    fullNotes: null,
    videoLink: null,
  },
  {
    releaseId: 11,
    platform: RELEASE_PLATFORM.DASHBOARD,
    releaseDate: 'Winter 2026',
    status: RELEASE_STATUS.UPCOMING,
    shortNotes: 'Coaches will be able to view habit tracking for clients',
    fullNotes: null,
    videoLink: null,
  },
  {
    releaseId: 15,
    platform: RELEASE_PLATFORM.DASHBOARD,
    releaseDate: 'Winter 2026',
    status: RELEASE_STATUS.UPCOMING,
    shortNotes: 'Coaches will be able to view food logs for clients on their dashboard',
    fullNotes: null,
    videoLink: null,
  },
  {
    releaseId: 14,
    platform: RELEASE_PLATFORM.APP,
    releaseDate: 'Winter 2026',
    status: RELEASE_STATUS.UPCOMING,
    shortNotes: 'Clients will be able to track the food they eat inside the SWYMO app',
    fullNotes: null,
    videoLink: null,
  },
  {
    releaseId: 17,
    platform: RELEASE_PLATFORM.DASHBOARD,
    releaseDate: 'Spring 2027',
    status: RELEASE_STATUS.UPCOMING,
    shortNotes:
      'Coaches will be able to view wearable device data for clients on their dashboard',
    fullNotes: null,
    videoLink: null,
  },
  {
    releaseId: 16,
    platform: RELEASE_PLATFORM.APP,
    releaseDate: 'Spring 2027',
    status: RELEASE_STATUS.UPCOMING,
    shortNotes: 'Clients will be able to integrate wearable devices into their SWYMO app',
    fullNotes: null,
    videoLink: null,
  },
];
